import { RouteProps } from '../../routes/AppRouter'
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Select as MUSelect,
  TextField,
  Typography,
} from '@mui/material'
import { v4 as uuidv4 } from 'uuid'
import { useForm } from '../../common/utils/form-generation/useForm'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { URL_CLIENTS } from '../../routes/routes-constants'
import { useEffect, useState } from 'react'
import { FormAction, FormActions } from '../../common/utils/form-generation'
import { useStyles } from './Client.styles'
import { COLOR_PRIMARY } from '../../routes/color-constants'
import { getClientContainer } from '../../container/client-modules'
import { ClientService } from '../../modules/clients/services/ClientService'
import { CLIENTS_SERVICE_KEY } from '../../modules/clients'
import { Client, ClientOdoo, FatherClinic } from '../../modules/clients/models/Client'
import { makeStyles } from '@mui/styles'
import { Theme } from '@mui/material/styles'
import { ChangePasswordDTO } from '../../modules/auth/models/ChangePassword'
import { Lock } from '@mui/icons-material'
import { AUTH_SERVICE_KEY } from '../../modules/auth'
import { AuthService } from '../../modules/auth/services/AuthService'
import { getAuthContainer } from '../../container/auth-modules'
import { clinicUserRoles, roleTypes } from '../../modules/users/enums/RoleType'
import { Permission } from '../../common/enums/Permissions'
import { useSnackbar } from 'notistack'
import Autocomplete from '@mui/material/Autocomplete'
import { getCountries } from './getCountires'
import AddBoxIcon from '@mui/icons-material/AddBox'
import CancelIcon from '@mui/icons-material/Cancel'
import { getUserContainer } from 'container/user-modules'
import { UserService } from 'modules/users/services/UserService'
import { USER_SERVICE_KEY } from 'modules/users'
import { emptyRegisterUser, UserRegister } from 'modules/users/models/User'
import { LoadingSpinner } from '../../components/loading-spinner/LoadingSpinner'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import { Query } from 'common/api/Query'
import { ClientDTO, emptyClientDTO, emptyFatherClinic, FatherClinicDTO } from 'modules/clients/models/ClientDTO'
import Select from 'react-select'

const clientService = getClientContainer().get<ClientService>(CLIENTS_SERVICE_KEY)
const authService = getAuthContainer().get<AuthService>(AUTH_SERVICE_KEY)
const userService = getUserContainer().get<UserService>(USER_SERVICE_KEY)

// ─── Types ────────────────────────────────────────────────────────────────────

export type ClientFormProps = RouteProps & {
  client: Client | undefined
}

type ClinicEntry = {
  id: string
  data: ClientDTO
  users: UserRegister[]
}

const emptyClinicEntry = (): ClinicEntry => ({
  id: uuidv4(),
  data: emptyClientDTO(),
  users: [],
})

// ─── Component ────────────────────────────────────────────────────────────────

export const Form = (props: ClientFormProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const classes = useStyles({ color: COLOR_PRIMARY })
  const { enqueueSnackbar } = useSnackbar()
  const countries = getCountries()

  // ─── Detectar modo ────────────────────────────────────────────────────────
  const urlParams = new URLSearchParams(window.location.search)
  const isFatherEdit = urlParams.get('type') === 'father'

  const pathParts = window.location.pathname.split('/')
  const idFromUrl = pathParts[pathParts.length - 1]
  const fatherEditId = isFatherEdit ? idFromUrl : undefined

  // Modo edición clínica individual: tiene ID en URL, no es father, no es 'create'
  const isClinicEdit = !isFatherEdit && !!props.client?.id

  // Modo creación: no hay ID (o es 'create') y no es father
  const isNew = !isFatherEdit && !isClinicEdit

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isLoadingClients, setIsLoadingClients] = useState<boolean>(true)
  const roles = authService.get().permissions.find((p) => p === Permission.editAllUsers)
    ? roleTypes()
    : clinicUserRoles()

  const [passwordModal, setPasswordModal] = useState<boolean>(false)
  const [usersBefore, setUsersBefore] = useState<UserRegister[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [clientsOdoo, setClientsOdoo] = useState<ClientOdoo[]>([])
  const [fatherName, setFatherName] = useState<string>('')
  const [clinics, setClinics] = useState<ClinicEntry[]>([emptyClinicEntry()])

  const useClass = makeStyles((theme: Theme) =>
    ({
      modal: {
        position: 'absolute',
        width: 600,
        backgroundColor: theme.palette.background.paper,
        borderRadius: '4px',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 0),
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
      },
      plusIcon: { color: '#00b8e2', alignSelf: 'flex-end', cursor: 'pointer' },
      cancelIcon: { color: '#dbb4b4', alignSelf: 'flex-end', cursor: 'pointer' },
      loadingSpinner: {
        position: 'absolute',
        right: '40%',
        overflow: 'hidden !important',
        bottom: '30%',
        color: '#0077ff !important',
      },
      clinicCard: {
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        padding: theme.spacing(3),
        marginTop: theme.spacing(3),
        position: 'relative',
        backgroundColor: '#fafafa',
      },
      clinicCardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
      },
    })
  )

  const formClass = useClass()

  // ─── Effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    clientService.getClinicsOdoo().subscribe((res) => {
      setClientsOdoo(res.items)
      setIsLoadingClients(false)
    })
  }, [])

  useEffect(() => {
    const filterID = props.client?.id ?? ''
    clientService.getFilteredList(new Query({})).subscribe((res) => {
      setClients(filterID ? res.items.filter((c) => c.id !== filterID) : res.items)
    })
  }, [props.client])

  // ── MODO EDICIÓN FATHER CLINIC ─────────────────────────────────────────────
  useEffect(() => {
    if (!isFatherEdit) return

    if (!fatherEditId) {
      setIsLoading(false)
      return
    }

    clientService.getFatherByID(fatherEditId).subscribe((father) => {
      if (father) setFatherName(father.name)
    })

    clientService.getFilteredList(new Query({})).subscribe((res) => {
      const subClinics = res.items.filter((c) => c.father === fatherEditId)

      if (subClinics.length === 0) {
        setClinics([emptyClinicEntry()])
        setIsLoading(false)
        return
      }

      Promise.all(
        subClinics.map((clinic) => clientService.getClinicAndUsers(clinic.id!).toPromise())
      ).then((results) => {
        const loaded: ClinicEntry[] = results
          .filter((r) => !!r)
          .map((r) => ({
            id: r!.clinic.id!,
            data: r!.clinic,
            users: r!.clinicUsers ?? [],
          }))

        setClinics(loaded.length > 0 ? loaded : [emptyClinicEntry()])
        setUsersBefore(JSON.parse(JSON.stringify(results.flatMap((r) => r?.clinicUsers ?? []))))
        setIsLoading(false)
      })
    })
  }, [fatherEditId])

  // ── MODO EDICIÓN CLÍNICA INDIVIDUAL ───────────────────────────────────────
  useEffect(() => {
    if (!isClinicEdit) return

    clientService.getClinicAndUsers(props.client!.id!).subscribe((res) => {
      if (res) {
        setClinics([{
          id: props.client!.id!,
          data: res.clinic,
          users: res.clinicUsers ?? [],
        }])
        setUsersBefore(JSON.parse(JSON.stringify(res.clinicUsers ?? [])))
        setIsLoading(false)
      }
    })
  }, [props.client])

  // ── MODO CREACIÓN NUEVA ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isNew) return
    setClinics([emptyClinicEntry()])
    setIsLoading(false)
  }, [props.client])

  // ─── Clinic helpers ───────────────────────────────────────────────────────

  const handleAddClinic = () => setClinics((prev) => [...prev, emptyClinicEntry()])

  const handleRemoveClinic = (clinicIndex: number) =>
    setClinics((prev) => prev.filter((_, i) => i !== clinicIndex))

  const handleClinicDataChange = (clinicIndex: number, field: string, value: any) =>
    setClinics((prev) =>
      prev.map((clinic, i) =>
        i === clinicIndex ? { ...clinic, data: { ...clinic.data, [field]: value } } : clinic
      )
    )

  const handleClinicSetData = (clinicIndex: number, newData: Partial<ClientDTO>) =>
    setClinics((prev) =>
      prev.map((clinic, i) =>
        i === clinicIndex ? { ...clinic, data: { ...clinic.data, ...newData } } : clinic
      )
    )

  // ─── User helpers ─────────────────────────────────────────────────────────

  const handleAddUser = (clinicIndex: number) =>
    setClinics((prev) =>
      prev.map((clinic, i) =>
        i === clinicIndex ? { ...clinic, users: [...clinic.users, emptyRegisterUser()] } : clinic
      )
    )

  const handleChangeUser = (field: string, value: any, clinicIndex: number, userIndex: number) =>
    setClinics((prev) =>
      prev.map((clinic, ci) => {
        if (ci !== clinicIndex) return clinic
        return {
          ...clinic,
          users: clinic.users.map((user, ui) =>
            ui === userIndex ? { ...user, [field]: value } : user
          ),
        }
      })
    )

  const removeUser = (clinicIndex: number, userIndex: number) =>
    setClinics((prev) =>
      prev.map((clinic, ci) => {
        if (ci !== clinicIndex) return clinic
        return { ...clinic, users: clinic.users.filter((_, ui) => ui !== userIndex) }
      })
    )

  // ─── Odoo select helper ───────────────────────────────────────────────────

  const getTaxName = (clinicData: ClientDTO) => {
    const filtered = clientsOdoo.filter((c) => c.value === clinicData.idOdoo)
    if (filtered.length > 0) {
      return { label: filtered[filtered.length - 1].label, value: filtered[filtered.length - 1].value }
    }
    return { label: '', value: '' }
  }

  // ─── Comparison helpers ───────────────────────────────────────────────────

  const areEquals = (oldUser: UserRegister, newUser: UserRegister): boolean => {
    if (
      oldUser.firstName !== newUser.firstName ||
      oldUser.lastName !== newUser.lastName ||
      oldUser.email !== newUser.email ||
      oldUser.role !== newUser.role
    ) return false
    if (oldUser.emails?.length !== newUser.emails?.length) return false
    return oldUser.emails?.every((email, i) => email === newUser.emails[i]) ?? true
  }

  const compareArrays = (arrayOld: UserRegister[], arrayUpdated: UserRegister[]) => ({
    itemsToAdd: arrayUpdated.filter((u) => !arrayOld.some((o) => o.id === u.id)),
    itemsToDelete: arrayOld.filter((o) => !arrayUpdated.some((u) => u.id === o.id)),
  })

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    for (let i = 0; i < clinics.length; i++) {
      const clinic = clinics[i]
      const label = `${t('clinic')} ${i + 1}`

      if (
        !clinic.data.taxIdentity || !clinic.data.taxName || !clinic.data.street ||
        !clinic.data.number || !clinic.data.zipCode || !clinic.data.city ||
        !clinic.data.province || !clinic.data.country
      ) {
        enqueueSnackbar(`${label}: ${t('fillRequiredFields')}`, { variant: 'error' })
        return
      }

      if (clinic.users.length === 0) {
        enqueueSnackbar(`${label}: ${t('mustExistsOneUser')}`, { variant: 'error' })
        return
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{1,}$/
      for (let j = 0; j < clinic.users.length; j++) {
        const user = clinic.users[j]
        if (!user.firstName || !user.lastName || !user.email || !user.role) {
          enqueueSnackbar(
            `${label} - ${t('theUser')} ${j + 1} ${t('dontHaveAllTheFields')}`,
            { variant: 'error' }
          )
          return
        }
        if (!emailRegex.test(user.email)) {
          enqueueSnackbar(`${t('theEmail')} ${user.email} ${t('isnotvalid')}`, { variant: 'error' })
          return
        }
      }
    }

    setIsLoading(true)

    try {
      // ── EDICIÓN CLÍNICA INDIVIDUAL ───────────────────────────────────────
      if (isClinicEdit) {
        const clinic = clinics[0]
        const res = await clientService.update(clinic.data).toPromise()
        if (res === undefined) {
          enqueueSnackbar(t('errorRegistering'), { variant: 'error' })
          return
        }

        const { itemsToAdd, itemsToDelete } = compareArrays(usersBefore, clinic.users)
        await Promise.all(itemsToDelete.map((u) => userService.delete(u.id, clinic.id ).toPromise()))
        await Promise.all(
          itemsToAdd.map((u) =>
            userService.addClinicUser({ ...u, clinicID: clinic.data.id!, role: Number(u.role) }).toPromise()
          )
        )
        await Promise.all(
          clinic.users.map((u) => {
            const before = usersBefore.find((ub) => ub.id === u.id)
            if (before && !areEquals(before, u)) return userService.updateClinicUser(u).toPromise()
            return Promise.resolve(null)
          })
        )

      // ── EDICIÓN FATHER CLINIC ────────────────────────────────────────────
      } else if (isFatherEdit && fatherEditId) {
        const updatedFather: FatherClinicDTO = { id: fatherEditId, name: fatherName }
        const fatherRes = await clientService.updateFather(updatedFather).toPromise()
        if (fatherRes === undefined) {
          enqueueSnackbar(t('errorRegistering'), { variant: 'error' })
          return
        }

        for (const clinic of clinics) {
          const clinicUsersBefore = usersBefore.filter((u) => (u as any).clinicID === clinic.id)
          const isNewClinic = !clinic.data.id || (!usersBefore.some((u) => (u as any).clinicID === clinic.id))

          if (isNewClinic) {
            clinic.data.id = uuidv4()
            clinic.data.father = fatherEditId
            const res = await clientService.add(clinic.data).toPromise()
            if (res === undefined) {
              enqueueSnackbar(t('errorRegistering'), { variant: 'error' })
              continue
            }
            const usersWithClinic = clinic.users.map((u) => ({
              ...u, clinicID: clinic.data.id!, role: Number(u.role),
            }))
            await userService.addClinicUsers(usersWithClinic).toPromise()
          } else {
            const res = await clientService.update(clinic.data).toPromise()
            if (res === undefined) {
              enqueueSnackbar(t('errorRegistering'), { variant: 'error' })
              continue
            }
            const { itemsToAdd, itemsToDelete } = compareArrays(clinicUsersBefore, clinic.users)
            await Promise.all(itemsToDelete.map((u) => userService.delete(u.id, clinic.id).toPromise()))
            await Promise.all(
              itemsToAdd.map((u) =>
                userService.addClinicUser({ ...u, clinicID: clinic.data.id!, role: Number(u.role) }).toPromise()
              )
            )
            await Promise.all(
              clinic.users.map((u) => {
                const before = clinicUsersBefore.find((ub) => ub.id === u.id)
                if (before && !areEquals(before, u)) return userService.updateClinicUser(u).toPromise()
                return Promise.resolve(null)
              })
            )
          }
        }

      // ── CREACIÓN NUEVA ───────────────────────────────────────────────────
      } else {
        const newFather = emptyFatherClinic(fatherName)
        const fatherRes = await clientService.addFather(newFather).toPromise()
        if (fatherRes === undefined) {
          enqueueSnackbar(t('errorRegistering'), { variant: 'error' })
          return
        }

        for (const clinic of clinics) {
          clinic.data.id = uuidv4()
          clinic.data.father = newFather.id

          const res = await clientService.add(clinic.data).toPromise()
          if (res === undefined) {
            enqueueSnackbar(t('errorRegistering'), { variant: 'error' })
            continue
          }

          const usersWithClinic = clinic.users.map((u) => ({
            ...u, clinicID: clinic.data.id!, role: Number(u.role),
          }))
          const res2 = await userService.addClinicUsers(usersWithClinic).toPromise()
          if (res2 === undefined) {
            enqueueSnackbar(t('errorRegisteringUsers'), { variant: 'error' })
          }
        }
      }
    } finally {
      setIsLoading(false)
      goToClients()
    }
  }

  const goToClients = () => navigate(URL_CLIENTS)

  const actions: FormAction[] = [
    { label: t('back'), handleAction: () => goToClients() },
  ]

  // ─── Password form ────────────────────────────────────────────────────────

  const isPasswordValid = (value: string): boolean => dataPassword.newPassword === value

  const {
    handleChange: handleChangePassword,
    handleSubmit: handleSubmitPassword,
    data: dataPassword,
    errors: errorsPassword,
  } = useForm<ChangePasswordDTO>({
    validations: {
      newPassword: {
        required: { value: true, message: t('newPasswordIsRequired') },
        pattern: {
          value: '^(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z]).{8,}$',
          message: t('passwordRequirements'),
        },
      },
      confirmNewPassword: {
        required: { value: true, message: t('repeatNewPasswordIsRequired') },
        custom: { isValid: isPasswordValid, message: t('passwordsDontMatch') },
      },
    },
    onSubmit: () => {
      clinics[0]?.users.forEach((user) => {
        authService.changePassword({ ...dataPassword, id: user.id })
      })
      setPasswordModal(false)
    },
  })

  const actionsPassword: FormAction[] = [
    { label: t('close'), handleAction: () => setPasswordModal(false) },
  ]



  return (
    <>
      {!isLoading && !isLoadingClients ? (
        <form onSubmit={handleSubmit} style={{ marginBottom: '3.5%' }}>



          {/* Nombre del grupo padre — oculto en edición individual */}
          {!isClinicEdit && (
            <Grid container spacing={5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  id="fatherName"
                  label={t('fatherClinicName')}
                  value={fatherName}
                  onChange={(e) => setFatherName(e.target.value)}
                />
              </Grid>
            </Grid>
          )}

          {/* Lista de sub-clínicas */}
          {clinics.map((clinic, clinicIndex) => (
            <Box key={clinic.id} className={formClass.clinicCard}>
              <Box className={formClass.clinicCardHeader}>
                {/* En edición individual no mostramos "Clínica N", solo el nombre */}
                <Typography variant="h6">
                  {isClinicEdit ? t('clinicData') : `${t('clinic')} ${clinicIndex + 1}`}
                </Typography>
                {!isClinicEdit && clinics.length > 1 && (
                  <CancelIcon
                    className={formClass.cancelIcon}
                    style={{ fontSize: '28px', cursor: 'pointer' }}
                    onClick={() => handleRemoveClinic(clinicIndex)}
                    titleAccess={t('removeClinic')}
                  />
                )}
              </Box>

              <Grid container spacing={5}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t('taxIdentity')}
                    value={clinic.data.taxIdentity || ''}
                    onChange={(e) => handleClinicDataChange(clinicIndex, 'taxIdentity', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Select
                    options={clientsOdoo}
                    placeholder={t('taxName')}
                    onChange={(newValue: any) =>
                      handleClinicSetData(clinicIndex, {
                        taxName: newValue?.label || '',
                        idOdoo: newValue?.value || '',
                      })
                    }
                    value={getTaxName(clinic.data)}
                    styles={{
                      control: (base: any) => ({ ...base, textAlign: 'left', height: '56px' }),
                      menu: (base: any) => ({ ...base, zIndex: 10 }),
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t('street')}
                    value={clinic.data.street || ''}
                    onChange={(e) => handleClinicDataChange(clinicIndex, 'street', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t('number')}
                    value={clinic.data.number || ''}
                    onChange={(e) => handleClinicDataChange(clinicIndex, 'number', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t('zipCode')}
                    value={clinic.data.zipCode || ''}
                    onChange={(e) => handleClinicDataChange(clinicIndex, 'zipCode', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t('city')}
                    value={clinic.data.city || ''}
                    onChange={(e) => handleClinicDataChange(clinicIndex, 'city', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t('province')}
                    value={clinic.data.province || ''}
                    onChange={(e) => handleClinicDataChange(clinicIndex, 'province', e.target.value)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    disablePortal
                    fullWidth
                    options={countries}
                    value={{ label: t(clinic.data.country) || '', value: clinic.data.country || '' }}
                    onChange={(_, value) =>
                      handleClinicDataChange(clinicIndex, 'country', (value as any)?.value)
                    }
                    renderInput={(params) => (
                      <TextField variant="outlined" {...params} label={t('country')} />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t('observations')}
                    value={clinic.data.observations || ''}
                    onChange={(e) => handleClinicDataChange(clinicIndex, 'observations', e.target.value)}
                  />
                </Grid>

                {/* Notifications */}
                <Grid item xs={12}>
                  <FormControl style={{ display: 'flex', flexDirection: 'row' }}>
                    <FormLabel style={{ color: 'black', alignSelf: 'center' }}>
                      {t('notifications')}:
                    </FormLabel>
                    <RadioGroup style={{ display: 'flex', flexDirection: 'row', marginLeft: '1%' }}>
                      <FormControlLabel
                        value="stateChanges"
                        control={
                          <Radio
                            checked={clinic.data.cosnotifications}
                            onClick={() =>
                              handleClinicDataChange(
                                clinicIndex,
                                'cosnotifications',
                                !clinic.data.cosnotifications
                              )
                            }
                          />
                        }
                        label={t('stateChanges')}
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>

                {/* Referenced client */}
                {authService.userCan(Permission.assignOtherClientToClient) && (
                  <Grid item xs={6} style={{ display: 'flex', flexDirection: 'column' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!clinic.data.referencedClientID}
                          onChange={() =>
                            handleClinicSetData(clinicIndex, {
                              referencedClientID: clinic.data.referencedClientID ? null : '',
                            })
                          }
                        />
                      }
                      label={t('otherClient')}
                    />
                    {!!clinic.data.referencedClientID !== false &&
                      clinic.data.referencedClientID !== undefined && (
                        <FormControl fullWidth variant="outlined" style={{ marginTop: '3%' }}>
                          <InputLabel>{t('selectClient')}</InputLabel>
                          <MUSelect
                            fullWidth
                            style={{ textAlign: 'left' }}
                            value={clinic.data.referencedClientID || ''}
                            onChange={(e) =>
                              handleClinicDataChange(clinicIndex, 'referencedClientID', e.target.value)
                            }
                            label={t('client')}>
                            {clients.map((c) => (
                              <MenuItem value={c.id} key={c.id}>
                                {c.taxName}
                              </MenuItem>
                            ))}
                          </MUSelect>
                        </FormControl>
                      )}
                  </Grid>
                )}
              </Grid>

              {/* Users section */}
              <Grid container style={{ marginTop: '3%' }} xs={12}>
                <Grid item xs={6} style={{ display: 'flex', justifyContent: 'left' }}>
                  <Typography className={classes.title} variant="h6" align="left">
                    {t('users')}
                  </Typography>
                </Grid>
                <Grid item xs={6} style={{ justifyContent: 'flex-end', display: 'flex' }}>
                  <p style={{ fontSize: '12px', alignSelf: 'center', marginRight: '0.5%' }}>
                    {t('addUser')}
                  </p>
                  <AddBoxIcon
                    className={formClass.plusIcon}
                    style={{ fontSize: '34px', alignSelf: 'center' }}
                    onClick={() => handleAddUser(clinicIndex)}
                    titleAccess={t('addUser')}
                  />
                </Grid>
              </Grid>

              {clinic.users.map((user, userIndex) => (
                <Grid container spacing={5} key={user.id} style={{ marginTop: '1%' }}>
                  <Grid
                    xs={12}
                    style={{ justifyContent: 'flex-end', display: 'flex', marginBottom: '-1.5%' }}>
                    <CancelIcon
                      className={formClass.cancelIcon}
                      onClick={() => removeUser(clinicIndex, userIndex)}
                      titleAccess={t('removeUser')}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={t('firstName')}
                      value={user.firstName || ''}
                      onChange={(e) => handleChangeUser('firstName', e.target.value, clinicIndex, userIndex)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={t('lastName')}
                      value={user.lastName || ''}
                      onChange={(e) => handleChangeUser('lastName', e.target.value, clinicIndex, userIndex)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      type="email"
                      label={t('email')}
                      value={user.email || ''}
                      onChange={(e) => handleChangeUser('email', e.target.value, clinicIndex, userIndex)}
                      disabled={
                        (isClinicEdit || isFatherEdit) &&
                        usersBefore.some((u) => u.id === user.id)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel>{t('role')}</InputLabel>
                      <MUSelect
                        fullWidth
                        style={{ textAlign: 'left' }}
                        value={user.role || ''}
                        onChange={(e) =>
                          handleChangeUser('role', Number(e.target.value), clinicIndex, userIndex)
                        }
                        label={t('role')}>
                        {Object.entries(roles).map(([key, value]) => (
                          <MenuItem value={key} key={key}>
                            {value}
                          </MenuItem>
                        ))}
                      </MUSelect>
                    </FormControl>
                  </Grid>
                </Grid>
              ))}
            </Box>
          ))}

          {/* Añadir clínica — solo visible en creación y edición father */}
          {!isClinicEdit && (
            <Box style={{ display: 'flex', alignItems: 'center', marginTop: '2%' }}>
              <p style={{ fontSize: '13px', marginRight: '0.5%' }}>{t('addClinic')}</p>
              <AddBoxIcon
                className={formClass.plusIcon}
                style={{ fontSize: '36px' }}
                onClick={handleAddClinic}
                titleAccess={t('addClinic')}
              />
            </Box>
          )}

          <FormActions actions={actions} />
        </form>
      ) : (
        <LoadingSpinner className={formClass.loadingSpinner} />
      )}

      {/* Password modal */}
      <Modal open={passwordModal}>
        <Box className={formClass.modal}>
          <form onSubmit={handleSubmitPassword}>
            <Box mt={2}>
              <FormControl fullWidth variant="outlined" error={!!errorsPassword['newPassword']}>
                <TextField
                  fullWidth
                  variant="outlined"
                  onChange={(e) => handleChangePassword('newPassword', e.target.value)}
                  label={t('newPassword')}
                  type="password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormHelperText>{errorsPassword['newPassword']}</FormHelperText>
              </FormControl>
            </Box>
            <Box mt={2}>
              <FormControl fullWidth variant="outlined" error={!!errorsPassword['confirmNewPassword']}>
                <TextField
                  fullWidth
                  variant="outlined"
                  onChange={(e) => handleChangePassword('confirmNewPassword', e.target.value)}
                  label={t('confirmNewPassword')}
                  type="password"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                  }}
                />
                <FormHelperText>{errorsPassword['confirmNewPassword']}</FormHelperText>
              </FormControl>
            </Box>
            <FormActions actions={actionsPassword} />
          </form>
        </Box>
      </Modal>
    </>
  )
}
